import { Logger } from '@nextgen-web-framework/core';
import { getProductReqObj, getProductArrayReqObj } from '../config/productEndPoints';
import type { Products, ProductData } from '../models';
import { type CustomRequestOptions, ProjectionType } from '../../types';
import { handleRequest } from '../../transformers';
import { logObject } from '../../utils';

const API_VERSION_V1 = 'v1';

type IProductAPI = {
  productId: string;
  projections?: string;
  warehouseId?: string;
  fulfillmentType?: string;
  language?: string;
  channel?: string;
};

type IDynamicProductAPI = {
  productId: string;
  projections?: string;
  warehouseId?: string;
  userSegment?: string;
  fulfillmentType?: string;
};

type IProductAPIArray = (
  | {
      idList: string[];
      itemNumberList?: string[];
      variantGroupIdList?: string[];
    }
  | {
      idList?: string[];
      itemNumberList: string[];
      variantGroupIdList?: string[];
    }
  | {
      idList?: string[];
      itemNumberList?: string[];
      variantGroupIdList: string[];
    }
) & {
  projections?: string;
  fulfillmentType?: string;
  warehouseId?: string;
};

const loggerInstance = Logger.getInstance(logObject('productApi'));

export const productApi = {
  async getProductByIdOrError(
    {
      productId,
      projections = ProjectionType.Detailed,
      warehouseId = '',
      fulfillmentType,
      language,
      channel,
    }: IProductAPI,
    options: CustomRequestOptions = {},
  ): Promise<ProductData | Response> {
    if (!productId) {
      loggerInstance.error('Missing product ID. Please check arguments for a valid product ID.');
      throw new Error('Null argument. Please retry with valid product id.');
    }

    try {
      const requestObj = getProductReqObj(API_VERSION_V1, productId, language, channel);
      let errorResponse: Response | undefined;

      requestObj.params = {
        projections,
        warehouseId,
      };
      if (fulfillmentType) {
        requestObj.params.fulfillmentType = fulfillmentType;
      }

      options.errorHandlers = {
        default: (res: Response) => {
          errorResponse = res;
        },
      };

      const response = await handleRequest<ProductData>(requestObj, options);
      return errorResponse ? errorResponse : response;
    } catch (err) {
      loggerInstance.error(`Failed to create product request object. Message: ${err}`);
      throw err;
    }
  },
  async getProductByIds(
    {
      idList = [],
      itemNumberList = [],
      variantGroupIdList = [],
      projections = ProjectionType.Detailed,
      warehouseId = '',
      fulfillmentType,
    }: IProductAPIArray,
    options?: CustomRequestOptions,
  ): Promise<Products> {
    try {
      if ([idList, itemNumberList, variantGroupIdList].every((arr) => arr && arr.length === 0)) {
        loggerInstance.error('Please check arguments');
        throw null;
      }

      const requestObj = getProductArrayReqObj(API_VERSION_V1);
      requestObj.params = {
        projections,
        warehouseId,
      };

      if (fulfillmentType) {
        requestObj.params.fulfillmentType = fulfillmentType;
      }

      const ids = idList.toString();
      if (ids) {
        requestObj.params = {
          ...requestObj.params,
          ids,
        };
      }

      const itemNumbers = itemNumberList.toString();
      if (itemNumbers) {
        requestObj.params = {
          ...requestObj.params,
          itemNumbers,
        };
      }

      const variantGroupIds = variantGroupIdList.toString();
      if (variantGroupIds) {
        requestObj.params = {
          ...requestObj.params,
          variantGroupIds,
        };
      }

      return await handleRequest<Products>(requestObj, options);
    } catch (err) {
      loggerInstance.error(err);
      throw null;
    }
  },
  async getDynamicProductById(
    {
      productId,
      projections = [
        ProjectionType.ProductMin,
        ProjectionType.PriceMin,
        ProjectionType.InventoryMin,
        ProjectionType.ItemAll,
      ].join(','),
      warehouseId = '',
      userSegment = 'CUSTOMER',
      fulfillmentType,
    }: IDynamicProductAPI,
    options?: CustomRequestOptions,
  ): Promise<ProductData> {
    if (!productId) {
      loggerInstance.error('Missing product ID. Please check arguments for a valid product ID.');
      throw new Error('Null argument. Please retry with valid product id.');
    }
    try {
      const requestObj = getProductReqObj(API_VERSION_V1, productId);
      requestObj.params = {
        projections,
        warehouseId,
        userSegment,
      };
      if (fulfillmentType) {
        requestObj.params.fulfillmentType = fulfillmentType;
      }
      return await handleRequest<ProductData>(requestObj, options);
    } catch (err) {
      loggerInstance.error(`Failed to create product request object. Message: ${err}`);
      throw err;
    }
  },
};

export default productApi;
export type { ProductData, IProductAPI };
